import { WORKSPACE_PANELS } from "./qmsDocumentWorkspacePanels";

/**
 * @param {string} panelId
 * @param {object} [context]
 * @param {boolean} [context.canManageStaff]
 * @param {boolean} [context.hasStaff]
 */
export function buildQmsDocumentWorkspaceTourSteps(panelId, context = {}) {
  const { canManageStaff = false, hasStaff = false } = context;

  const panelMeta =
    WORKSPACE_PANELS.find((panel) => panel.id === panelId) ||
    WORKSPACE_PANELS[0];

  const navStep = {
    id: "workspace-nav",
    target: '[data-tour="qms-doc-workspace-nav"]',
    placement: "right",
    title: "Workspace menu",
    body: "Use this menu to switch sections for the open document — preview, staff access, workflow history, quiz, and related documents.",
  };

  const guideStep = {
    id: "workspace-guide",
    target: '[data-tour="qms-doc-workspace-guide"]',
    placement: "bottom",
    title: "Section guide",
    body: "Guide always explains only the section you currently have selected in the menu — not the whole workspace at once.",
  };

  const doneStep = {
    id: "done",
    placement: "center",
    title: "You are ready",
    body: `You can reopen this guide anytime while ${panelMeta.label} is selected.`,
  };

  if (panelId === "preview") {
    return [
      {
        id: "preview-welcome",
        placement: "center",
        title: "Document preview",
        body: "This section shows the published file in a read-only viewer. Switch to other menu items when you need staff access or related document actions.",
      },
      navStep,
      {
        id: "preview-viewer",
        target: '[data-tour="qms-doc-workspace-preview"]',
        placement: "left",
        title: "Preview viewer",
        body: "PDF, Word, Excel, PowerPoint, and image files open here in read-only mode. Scroll and zoom inside the viewer to read the document.",
      },
      {
        id: "preview-download-print",
        target: '[data-tour="qms-doc-workspace-preview"]',
        placement: "left",
        title: "Download and print",
        body: "Use Download original above the preview to get the exact uploaded file (important for Word forms with fillable fields). The OnlyOffice viewer download may remove content controls. For images, right-click to save.",
      },
      guideStep,
      doneStep,
    ];
  }

  if (panelId === "libraryStaff") {
    const steps = [
      {
        id: "staff-welcome",
        placement: "center",
        title: "Document Library Staff",
        body: "Control which staff can see this published document in the library. Assignments apply to this document version.",
      },
      navStep,
    ];

    if (canManageStaff) {
      steps.push(
        {
          id: "staff-individual",
          target: '[data-tour="qms-doc-workspace-staff-grid"]',
          placement: "top",
          title: "Assign or remove one person",
          body: "Each card is one staff member. If they do not have access, click Grant access — the card turns assigned and they can see this document in the library. If they already have access, click Remove access to revoke it. Wait for Saving… to finish before clicking again.",
          when: () => hasStaff,
        },
        {
          id: "staff-bulk",
          target: '[data-tour="qms-doc-workspace-staff-bulk"]',
          placement: "bottom",
          title: "Bulk selection",
          body: "To update several people at once, tick the checkboxes on their cards (or use Select all visible), then click Grant or Remove in the bar above the list.",
          when: () => hasStaff,
        },
        {
          id: "staff-rail",
          target: '[data-tour="qms-doc-workspace-staff-rail"]',
          placement: "left",
          title: "Search, filter, and summary",
          body: "Search by name or role, filter by All / Assigned / Not assigned, and see assigned vs total counts on the right.",
        },
      );
    } else {
      steps.push({
        id: "staff-readonly",
        placement: "center",
        title: "View only",
        body: "You can review who has library access for this document, but your role cannot change assignments.",
      });
    }

    steps.push(guideStep, doneStep);
    return steps.filter((step) => (step.when ? step.when(context) : true));
  }

  return [
    {
      id: "section-welcome",
      placement: "center",
      title: panelMeta.label,
      body: `This section shows ${panelMeta.label.toLowerCase()} for the open published document. Use the menu to switch to other workspace areas.`,
    },
    navStep,
    {
      id: "section-panel",
      target: '[data-tour="qms-doc-workspace-panel"]',
      placement: "left",
      title: panelMeta.label,
      body: "The main area updates when you pick a different menu item. Scroll inside the panel if the content is taller than the window.",
    },
    guideStep,
    doneStep,
  ];
}
